import {
  SCREEN_RECORDING_STATUSES,
  type RecordingPauseReason,
  type ScreenRecordingStatus,
} from './shared-responder-v2-core-recording-port.types';

export const RECORDING_PAUSE_EFFECTS = {
  Pause: 'pause',
  Resume: 'resume',
  None: 'none',
} as const;

type RecordingPauseEffect =
  (typeof RECORDING_PAUSE_EFFECTS)[keyof typeof RECORDING_PAUSE_EFFECTS];

export interface RecordingPauseTransition {
  readonly reasons: ReadonlySet<RecordingPauseReason>;
  readonly effect: RecordingPauseEffect;
}

const CAPTURE_LIVE_STATUSES: ReadonlySet<ScreenRecordingStatus> = new Set([
  SCREEN_RECORDING_STATUSES.Recording,
  SCREEN_RECORDING_STATUSES.Paused,
]);

export function isRecordingCaptureLive(status: ScreenRecordingStatus): boolean {
  return CAPTURE_LIVE_STATUSES.has(status);
}

export function holdRecordingPause(
  reasons: ReadonlySet<RecordingPauseReason>,
  reason: RecordingPauseReason
): RecordingPauseTransition {
  if (reasons.has(reason)) {
    return { reasons, effect: RECORDING_PAUSE_EFFECTS.None };
  }

  const next = new Set(reasons);
  next.add(reason);

  return {
    reasons: next,
    effect:
      reasons.size === 0
        ? RECORDING_PAUSE_EFFECTS.Pause
        : RECORDING_PAUSE_EFFECTS.None,
  };
}

export function releaseRecordingPause(
  reasons: ReadonlySet<RecordingPauseReason>,
  reason: RecordingPauseReason
): RecordingPauseTransition {
  if (!reasons.has(reason)) {
    return { reasons, effect: RECORDING_PAUSE_EFFECTS.None };
  }

  const next = new Set(reasons);
  next.delete(reason);

  return {
    reasons: next,
    effect:
      next.size === 0
        ? RECORDING_PAUSE_EFFECTS.Resume
        : RECORDING_PAUSE_EFFECTS.None,
  };
}

export function releaseAllRecordingPauses(
  reasons: ReadonlySet<RecordingPauseReason>
): RecordingPauseTransition {
  return {
    reasons: new Set<RecordingPauseReason>(),
    effect:
      reasons.size === 0
        ? RECORDING_PAUSE_EFFECTS.None
        : RECORDING_PAUSE_EFFECTS.Resume,
  };
}

export function resolveRecordingStatus(args: {
  captureStatus: ScreenRecordingStatus;
  pauseReasons: ReadonlySet<RecordingPauseReason>;
}): ScreenRecordingStatus {
  if (
    args.captureStatus === SCREEN_RECORDING_STATUSES.Recording &&
    args.pauseReasons.size > 0
  ) {
    return SCREEN_RECORDING_STATUSES.Paused;
  }

  return args.captureStatus;
}
